import {
  Checkbox,
  NumberInput,
  TextInput,
} from "@datapay-by-datacom/design-system";
import type {
  CompanyOptionsProfile,
  CompanySettingsProfile,
  PayGroupOptionsProfile,
  PayGroupSettingsProfile,
} from "../../generated/core-api";
import type {
  CsErrors,
  OptionFieldDef,
  OptionGroup,
  OptionSection,
  PayGroupBooleanKey,
  PayGroupNumberKey,
  PgNumberErrors,
} from "../types";
import {
  CS_BOOLEAN_DEPENDENTS,
  CS_BOOLEAN_PARENT,
  fieldsForGroup,
} from "../types";
import { assertNever } from "../utils/assertNever";
import s from "./OptionsSection.module.css";

/**
 * `pg-boolean` keys whose falling edge (on → off) must also null a dependent
 * `pg-number` field, mirroring the `LeaveRequestEscalation` compound-field
 * pattern above. Without this, a hidden/unvalidated conditional number can be
 * silently re-submitted alongside its now-disabled parent.
 */
const PG_BOOLEAN_DEPENDENTS: Partial<Record<PayGroupBooleanKey, PayGroupNumberKey>> = {
  EnablePayRunAuthorisation: "MinNumOfPayRunAuthorisers",
  AutoEndDateUserAfterTermination: "DaysAfterTerminationDate",
  AutomaticallyFinaliseMainPayRun: "DaysToFinaliseAfterDCDate",
};

interface OptionsSectionProps {
  section: OptionSection;
  /** Which provider group's fields to render (Datapay or Direct Access). */
  group: OptionGroup;
  /** Keep the legacy list or use the redesign's two-column review rows. */
  presentation?: "legacy" | "review";
  /** Values for `kind: "boolean" | "escalation"` fields. */
  companyValues: CompanyOptionsProfile;
  onCompanyChange: (next: CompanyOptionsProfile) => void;
  /** Values for `kind: "pg-boolean" | "pg-number"` fields. */
  pgValues: PayGroupOptionsProfile | undefined;
  onPgChange: (next: PayGroupOptionsProfile) => void;
  /** Values for `kind: "cs-boolean" | "cs-string"` fields (CompanySettings sub-profile). */
  csValues: CompanySettingsProfile | undefined;
  onCsChange: (next: CompanySettingsProfile) => void;
  /** Values for `kind: "pg-settings-boolean"` fields (PayGroupSettings sub-profile). */
  pgsValues: PayGroupSettingsProfile | undefined;
  onPgsChange: (next: PayGroupSettingsProfile) => void;
  /** Validation error for the escalation-days compound field. */
  escalationError?: string;
  /**
   * Validation errors for Pay Group number fields, keyed by the field's
   * `PayGroupOptionsProfile` key (e.g. `"DaysAfterTerminationDate"`).
   */
  pgNumberErrors?: PgNumberErrors;
  /**
   * Validation errors for Company Settings string fields, keyed by the
   * field's `CompanySettingsProfile` string key (e.g.
   * `"SetDirectAccessAdditionalResourcesUrl"`).
   */
  csErrors?: CsErrors;
}

/**
 * Renders one section's fields for a single provider `group` as a sub-section:
 * an `<h4>` sub-heading (`section.title`) followed by the A→Z-sorted field list
 * for that group. Nested inside a provider tab or review card owned by
 * `CopySettingsProfile`.
 *
 * Returns `null` when the (section, group) pair has no fields, so an empty
 * sub-heading is never rendered.
 */
export function OptionsSection({
  section,
  group,
  presentation = "review",
  companyValues,
  onCompanyChange,
  pgValues,
  onPgChange,
  csValues,
  onCsChange,
  pgsValues,
  onPgsChange,
  escalationError,
  pgNumberErrors,
  csErrors,
}: OptionsSectionProps) {
  const setCompany = (patch: Partial<CompanyOptionsProfile>) =>
    onCompanyChange({ ...companyValues, ...patch });

  const setPg = (patch: Partial<PayGroupOptionsProfile>) =>
    onPgChange({ ...(pgValues ?? {}), ...patch });

  const setCs = (patch: Partial<CompanySettingsProfile>) =>
    onCsChange({ ...(csValues ?? {}), ...patch });

  const setPgs = (patch: Partial<PayGroupSettingsProfile>) =>
    onPgsChange({ ...(pgsValues ?? {}), ...patch });

  const fields = fieldsForGroup(section, group);
  if (fields.length === 0) {
    return null;
  }

  // Some fields share a display label with a field in the *other* section
  // (e.g. "Enable Annual Leave in Weeks Calculations" appears under both
  // Company options and Pay Group options — Christmas uses no disambiguating
  // suffix). Grouping each section's controls under role="group" +
  // aria-labelledby lets assistive tech announce which section a control
  // belongs to, without changing the visible label text.
  const headingId = `option-section-heading-${section.id}-${group}`;

  return (
    <div
      className={`${s.section} ${
        presentation === "review" ? s.reviewSection : ""
      }`}
    >
      <h4 id={headingId} className={s.heading}>
        {section.title}
      </h4>
      <div
        className={`${s.checkboxGroup} ${
          presentation === "review" ? s.reviewCheckboxGroup : ""
        }`}
        role="group"
        aria-labelledby={headingId}
      >
        {fields.map((field) =>
          renderField(field, {
            companyValues,
            setCompany,
            pgValues,
            setPg,
            csValues,
            setCs,
            pgsValues,
            setPgs,
            escalationError,
            pgNumberErrors,
            csErrors,
            presentation,
          })
        )}
      </div>
    </div>
  );
}

// ── Field renderer ─────────────────────────────────────────────────────────

interface RenderContext {
  companyValues: CompanyOptionsProfile;
  setCompany: (patch: Partial<CompanyOptionsProfile>) => void;
  pgValues: PayGroupOptionsProfile | undefined;
  setPg: (patch: Partial<PayGroupOptionsProfile>) => void;
  csValues: CompanySettingsProfile | undefined;
  setCs: (patch: Partial<CompanySettingsProfile>) => void;
  pgsValues: PayGroupSettingsProfile | undefined;
  setPgs: (patch: Partial<PayGroupSettingsProfile>) => void;
  escalationError: string | undefined;
  pgNumberErrors: PgNumberErrors | undefined;
  csErrors: CsErrors | undefined;
  presentation: "legacy" | "review";
}

interface BooleanOptionProps {
  label: string;
  isSelected: boolean;
  isDisabled?: boolean;
  presentation: "legacy" | "review";
  onChange: (isSelected: boolean) => void;
}

function BooleanOption({
  label,
  isSelected,
  isDisabled = false,
  presentation,
  onChange,
}: BooleanOptionProps) {
  const checkbox = (
    <Checkbox
      isSelected={isSelected}
      isDisabled={isDisabled}
      onChange={onChange}
    >
      {label}
    </Checkbox>
  );

  if (presentation === "legacy") {
    return checkbox;
  }

  return (
    <div className={`${s.optionRow} ${isDisabled ? s.optionRowDisabled : ""}`}>
      {checkbox}
    </div>
  );
}

function renderField(field: OptionFieldDef, ctx: RenderContext): React.ReactNode {
  const {
    companyValues,
    setCompany,
    pgValues,
    setPg,
    csValues,
    setCs,
    pgsValues,
    setPgs,
    escalationError,
    pgNumberErrors,
    csErrors,
    presentation,
  } = ctx;

  switch (field.kind) {
    case "boolean": {
      const isSelected = companyValues[field.key] ?? false;

      return (
        <BooleanOption
          key={field.key}
          label={field.label}
          isSelected={isSelected}
          presentation={presentation}
          onChange={(checked) => {
            setCompany({ [field.key]: checked });
          }}
        />
      );
    }

    case "escalation": {
      const escalationEnabled = companyValues.EnableLeaveRequestEscalation ?? false;

      return (
        <div
          key="escalation"
          className={presentation === "review" ? s.compoundOption : undefined}
        >
          <BooleanOption
            label={field.label}
            isSelected={escalationEnabled}
            presentation={presentation}
            onChange={(isSelected) =>
              setCompany({
                EnableLeaveRequestEscalation: isSelected,
                LeaveRequestEscalation: isSelected
                  ? companyValues.LeaveRequestEscalation
                  : null,
              })
            }
          />

          <div aria-live="polite">
            {escalationEnabled && (
              <div
                className={`${s.conditionalField} ${
                  presentation === "review" ? s.reviewConditionalField : ""
                }`}
              >
                <NumberInput
                  label="Escalation after (days)"
                  minValue={1}
                  isRequired
                  value={companyValues.LeaveRequestEscalation ?? undefined}
                  onChange={(value) =>
                    setCompany({
                      LeaveRequestEscalation: Number.isNaN(value) ? null : value,
                    })
                  }
                  errorMessage={escalationError}
                  isInvalid={Boolean(escalationError)}
                />
              </div>
            )}
          </div>
        </div>
      );
    }

    case "pg-boolean": {
      const isSelected = pgValues?.[field.key] ?? false;
      const dependentKey = PG_BOOLEAN_DEPENDENTS[field.key];

      return (
        <BooleanOption
          key={field.key}
          label={field.label}
          isSelected={isSelected}
          presentation={presentation}
          onChange={(checked) =>
            setPg({
              [field.key]: checked,
              // Clear the dependent conditional number when its parent is
              // disabled, so a stale value can't be silently re-submitted
              // alongside a hidden, unvalidated field.
              ...(dependentKey !== undefined && !checked ? { [dependentKey]: null } : {}),
            })
          }
        />
      );
    }

    case "pg-number": {
      // When a parentKey is set, only render when the parent boolean is true.
      if (field.parentKey !== undefined) {
        const parentOn = pgValues?.[field.parentKey] ?? false;
        if (!parentOn) {
          return null;
        }
      }

      const currentValue = pgValues?.[field.key] ?? undefined;
      const errorMessage = pgNumberErrors?.[field.key];

      return (
        <div
          key={field.key}
          className={`${presentation === "review" ? s.inputOption : ""} ${
            field.parentKey !== undefined ? s.conditionalField : ""
          } ${
            presentation === "review" && field.parentKey !== undefined
              ? s.reviewConditionalField
              : ""
          }`}
        >
          <NumberInput
            label={field.label}
            value={currentValue ?? undefined}
            minValue={field.min}
            maxValue={field.max}
            onChange={(value) =>
              setPg({ [field.key]: Number.isNaN(value) ? null : value })
            }
            errorMessage={errorMessage}
            isInvalid={Boolean(errorMessage)}
          />
        </div>
      );
    }

    case "cs-boolean": {
      const dependentKey = CS_BOOLEAN_DEPENDENTS[field.key];
      const parentKey = CS_BOOLEAN_PARENT[field.key];
      // CO-DA-14 → CO-DA-01 coupling (§5.4 sibling-dependency):
      // When this field has a parent key and that parent is off, disable this
      // checkbox and force its *displayed* value to `false`. This is a display
      // gate only — the stored value is normalised to `false` at submit time by
      // cleanProfile() (see MaintainCompanyConfigForm), so a config loaded with an
      // inconsistent {parent: false, dependent: true} pair can't persist the stale
      // `true`.
      const parentIsOn =
        parentKey !== undefined ? (csValues?.[parentKey] ?? false) : true;
      const isSelected = parentIsOn ? (csValues?.[field.key] ?? false) : false;

      return (
        <BooleanOption
          key={field.key}
          label={field.label}
          isSelected={isSelected}
          isDisabled={!parentIsOn}
          presentation={presentation}
          onChange={(checked) =>
            setCs({
              [field.key]: checked,
              // When this master key is turned off, also clear its dependent
              // so a stale `true` can't be silently re-submitted.
              ...(dependentKey !== undefined && !checked
                ? { [dependentKey]: false }
                : {}),
            })
          }
        />
      );
    }

    case "cs-string": {
      const urlValue = csValues?.[field.key] ?? "";
      const errorMessage = csErrors?.[field.key];
      return (
        <div
          key={field.key}
          className={presentation === "review" ? s.inputOption : undefined}
        >
          <TextInput
            label={field.label}
            value={urlValue}
            onChange={(raw) => {
              const trimmed = raw.trim();
              setCs({ [field.key]: trimmed === "" ? null : trimmed });
            }}
            errorMessage={errorMessage}
            isInvalid={Boolean(errorMessage)}
          />
        </div>
      );
    }

    case "pg-settings-boolean": {
      const isSelected = pgsValues?.[field.key] ?? false;

      return (
        <BooleanOption
          key={field.key}
          label={field.label}
          isSelected={isSelected}
          presentation={presentation}
          onChange={(checked) => setPgs({ [field.key]: checked })}
        />
      );
    }

    default:
      return assertNever(field);
  }
}
